import React, {useRef, useState, Fragment} from "react";
import {getPlatforms, isPlatform} from "@ionic/react";
// @ts-ignore
import * as d3_peaks from "d3-peaks";
// @ts-ignore
// import * as audioinput from "cordova-plugin-audioinput";
import html2canvas from 'html2canvas';
import {browserName, browserVersion} from "react-device-detect";
import {
    IonButton,
    IonButtons, IonFooter,
    IonImg,
    IonItem,
    IonLabel,
    IonSegment,
    IonSegmentButton,
    IonText,
    IonToolbar
} from "@ionic/react";
import Plot, {Figure} from "react-plotly.js";
import {getConstValues, getFrequencies, getTimes, getZeros, FFTAnalyzer} from "../dsp/FFTAnalyzer";
import {Datum, Layout, Shape} from "plotly.js";
// import MultiPlot from "./MultiPlot";

const isIOS = (isPlatform('ios') || isPlatform('iphone') || isPlatform('ipad'));
const isIOSApp = (isIOS && !isPlatform('mobileweb'));
const isIOSWeb = (isIOS && isPlatform('mobileweb'));
declare let audioinput: any;

const freqRes = 20.0;
const timeRes = 1/freqRes;
const recLen = 5.0; // Seconds of total recording time
const timeFrames = recLen * freqRes + 1;
const desiredSampleRate = 48000.0;
const audioCtxConstraints: AudioContextOptions = {
    latencyHint: "balanced",
    sampleRate: desiredSampleRate,
    // sinkId: {type: 'none'}
};

interface Props {
    highlights: Partial<Shape>[]
}

const AudioHandler: React.FC<Props> = ({highlights}) => {
    // Audio Analysis
    let [testing, setTesting] = useState(false);
    let [audioCtx, setAudioCtx] = useState<AudioContext | null>(null);
    let [audioStream, setAudioStream] = useState<MediaStream | null>(null);
    let sampleRate = useRef(desiredSampleRate);
    let procStateParam = useRef<AudioParam | undefined>();

    // Plot State
    const [ pData, setPData ] = useState<Plotly.Data[]>([]);
    const [ pLayout, setPLayout ] = useState<Partial<Plotly.Layout>>({});
    const [ pFrames, setPFrames ] = useState<Plotly.Frame[] | undefined>([]);
    const [ pConfig, setPConfig ] = useState<Partial<Plotly.Config> | undefined>({});
    // const [ graphType, setGraphType ] = useState('spectrum');
    const graphType = useRef('spectrum');
    const pRev = useRef(0);         // Plotly revision
    const dataRev = useRef(0);      // Plotly datarevision. Use this to ensure redrawing of the plot.
    const pFreqs = useRef<Float32Array>(new Float32Array(0));
    const pTimes = useRef<Float32Array>(new Float32Array(0));
    const pLevel = useRef<Float32Array>(new Float32Array(0));
    const pMag = useRef<Float32Array>(new Float32Array(0));
    const pWave = useRef<Float32Array>(new Float32Array(0));
    const pMagz = useRef<number[][]>([]);
    const pTimeIdx = useRef(0);

    // const [dataRev, setDataRev] = useState(0);      // Plotly datarevision. Use this to ensure redrawing of the plot.

    const getMicrophone = async () => {
        let frequencies = getFrequencies(freqRes, 1200, false);
        let levelHistory = getConstValues(-200, timeFrames);
        let times = getTimes(timeRes, timeFrames, false);
        let zMag: number[][] = [];
        let timeIndex = 0;
        let checkIndex = 0;
        let resonancesFound = 0;
        const findPeaks = d3_peaks.findPeaks()
            .kernel(d3_peaks.ricker)
            .gapThreshold(1)
            .minLineLength(1)
            .minSNR(1.0)
            .widths([1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20]);
        let decor = (v:any, i:any) => [v, i];          // set index to value
        let undecor = (a: any[]) => a[1];               // leave only index
        let argsort = (arr: any[]) => arr.map(decor).sort().map(undecor);

        const audioContext = new AudioContext(audioCtxConstraints);
        setAudioCtx(audioContext);
        await audioContext.audioWorklet.addModule('worklet/analyzer-worklet.js');
        console.log('audioContext.sampleRate = ' + audioContext.sampleRate);
        if(sampleRate.current !== audioContext.sampleRate)
        {
            sampleRate.current = audioContext.sampleRate;
            frequencies = getFrequencies(freqRes, sampleRate.current/freqRes/2, false);
        }
        for(let idx=0; idx<frequencies.length; idx++)
        {
            zMag[idx] = [];
        }
        const spectrumAnalyzer = new FFTAnalyzer(sampleRate.current/freqRes);
        const signalAnalyzerNode = new AudioWorkletNode(audioContext, 'analyzer-worklet');
        procStateParam.current = signalAnalyzerNode.parameters.get('state');
        signalAnalyzerNode.parameters.get('numFrames')?.setValueAtTime(sampleRate.current/freqRes, 0);

        const shiftData = () => {
            for(let jdx=0; jdx<zMag.length; jdx++)
            {
                zMag[jdx].shift();
            }
            for(let idx=0; idx<timeFrames-1; idx++)
            {
                levelHistory[idx] = levelHistory[idx+1];
            }
        }

        signalAnalyzerNode.port.onmessage = ({data}) => {
            const {message, rms, min, max, buffer, inData, inLength} = data;
            // console.log("Audio Proc Buffer: " + inData);
            // console.log("Audio Proc Buffer Min: " + min);
            // console.log("Audio Proc Buffer Max: " + max);
            // console.log("Audio Proc Buffer Length: " + inLength);
            // console.log("Audio Analyzer Buffer Length: " + buffer.length);
            const newData = spectrumAnalyzer.computeFFT(buffer);
            if(timeIndex >= timeFrames)
            {   // Shift the data in the buffers, to make it scroll in the plot
                timeIndex = timeFrames-1;
                shiftData();
            }
            for(let idx=0; idx<newData.length; idx++)
            {
                zMag[idx][timeIndex] = newData[idx];
            }
            levelHistory[timeIndex] = rms;

            pFreqs.current = frequencies;
            pTimes.current = times;
            pLevel.current = levelHistory;
            pMag.current = newData;
            pMagz.current = zMag;

            if(graphType.current === 'spectrum') {
                updateSignalPlot(frequencies, newData, null, ++dataRev.current);
            }
            else if(graphType.current === 'spectrogram')
            {
                updateSignalPlot(times, frequencies, zMag, ++dataRev.current);
            }
            else
            {
                updateSignalPlot(times, levelHistory.slice(0, timeIndex+1), null, ++dataRev.current);
            }
            timeIndex++;
            pTimeIdx.current = timeIndex;
        }

        const startNativeMic = () => {
            const captureCfg = {
                sampleRate: desiredSampleRate,
                bufferSize: 4096,//desiredSampleRate/freqRes,
                channels: audioinput.CHANNELS.MONO,
                // normalize: false, //true,
                // normalizationFactor: 32768.0,
                streamToWebAudio: false, //true,
                // audioContext: audioContext,
                audioSourceType: audioinput.AUDIOSOURCE_TYPE.UNPROCESSED,
            };
            // Start capturing audio from the microphone
            audioinput.start(captureCfg);
            procStateParam.current?.setValueAtTime(1, 0);
            audioinput.connect(signalAnalyzerNode);//.connect(audioContext.destination);

            setTesting(true);
        }
        console.log('isIOSApp: ' +  isIOSApp);
        if(isIOSApp)
        {   // Native iOS mic capture
            // Listen to audioinput events
            window.addEventListener( "audioinput", onAudioInput, false );
            // Listen to audioinputerror events
            window.addEventListener( "audioinputerror", onAudioInputError, false );

            audioinput.checkMicrophonePermission((hasPermission: boolean) => {
                if (hasPermission) {
                    console.log("We already have permission to record.");
                    startNativeMic();
                }
                else {
                    // Ask the user for permission to access the microphone
                    audioinput.getMicrophonePermission((hasPermission: boolean, message: any) => {
                        if (hasPermission) {
                            console.log("User granted us permission to record.");
                            startNativeMic();
                        } else {
                            console.warn("User denied permission to record.");
                            audioContext.close();
                            setTesting(false);
                        }
                    });
                }
            });
        }
        else if(navigator.mediaDevices !== undefined)
        {   // Web Audio mic capture
            const options = {
                video: false,
                audio: {
                    autoGainControl: false,
                    echoCancellation: false,
                    noiseSuppression: false,
                    // volume: 1.0,
                    sampleRate: desiredSampleRate,
                    suppressLocalAudioPlayback: true,
                }
            };
            try {
                const stream = await navigator.mediaDevices.getUserMedia(options);
                setAudioStream(stream);

                // navigator.mediaDevices.enumerateDevices().then((devices) => {
                //     console.log('available media devices:');
                //     devices.forEach((device) => {
                //         console.log(`${device.kind}: ${device.label} id = ${device.deviceId}`);
                //     })
                // })
                // console.log('supported constraints:');
                // const supportedConstraints = navigator.mediaDevices.getSupportedConstraints();
                // for (const constraint of Object.keys(supportedConstraints)) {
                //     console.log(constraint);
                // }
                // describeAudioChannels(stream);

                procStateParam.current?.setValueAtTime(1, 0);
                const audioSourceNode = audioContext.createMediaStreamSource(stream);
                audioSourceNode.connect(signalAnalyzerNode).connect(audioContext.destination);

                setTesting(true);
            }
            catch(err) {
                // error handling
                audioContext.close();
                setTesting(false);
            }
        }
    }

    const describeAudioChannels = (stream: MediaStream) => {
        console.log('audioStream: ' + stream);
        if(stream) {
            stream.getAudioTracks().forEach(track => {
                const constraints = track.getConstraints();
                for(const constraint in constraints) {
                    console.log('track constraints: ' + constraint);
                }
                if(browserName !== 'Firefox') {
                    const capabilities = track.getCapabilities();
                    for (const capability in capabilities) {
                        console.log('track capabilities: ' + capability);
                    }
                }
                const settings = track.getSettings();
                console.log('track.deviceId: ' + settings.deviceId);
                console.log('track.sampleRate: ' + settings.sampleRate);
                console.log('track.sampleSize: ' + settings.sampleSize);
                console.log('track.autoGainControl: ' + settings.autoGainControl);
                console.log('track.echoCancellation: ' + settings.echoCancellation);
                console.log('track.noiseSuppression: ' + settings.noiseSuppression);
                console.log('track.restrictOwnAudio: ' + settings.restrictOwnAudio);
            });
        }
    }

    const stopMicrophone = () => {
        procStateParam.current?.setValueAtTime(0, 0);
        if(isIOSApp)
        {
            audioinput.stop()
            audioinput.disconnect();
        }
        else if(audioStream) {
            audioStream.getAudioTracks().forEach(track => track.stop());
            setAudioStream(null);
        }
        audioCtx?.close();
        setAudioCtx(null);
        setTesting(false);
    }

    const toggleMicrophone = () => {
        if (testing) {
            stopMicrophone()
        } else {
            getMicrophone()
        }
    }

    const graphTypeAction = (ev: Event) => {
        const target = ev.target as HTMLIonSegmentElement;
        console.log('graphTypeAction');
        if(target) {
            console.log('graphTypeAction target found');
            graphType.current = target.value!.toLowerCase();
            if(graphType.current === 'spectrum') {
                updateSignalPlot(pFreqs.current, pMag.current, null, ++dataRev.current);
            }
            else if(graphType.current === 'spectrogram')
            {
                updateSignalPlot(pTimes.current, pFreqs.current, pMagz.current, ++dataRev.current);
            }
            else
            {
                updateSignalPlot(pTimes.current, pLevel.current.slice(0, pTimeIdx.current+1), null, ++dataRev.current);
            }
        }
    }

    const getLayout = (): Partial<Layout> => {
        pRev.current++;
        // console.log('getLayout pRev: ' + pRev.current);
        if(graphType.current === 'spectrum') {
            return {
                autosize: true,
                height: 320,
                title: {
                    text: 'Signal',
                    // automargin: false,
                },
                margin: {
                    // autoexpand: false,
                    t: 40,
                    b: 40,
                    l: 60,
                    r: 20,
                },
                showlegend: false,
                xaxis: {
                    // autorange: true,
                    range: [20, 20000],
                    fixedrange: true,
                    title: {
                        text: 'Frequency (Hz)',
                        standoff: 0,
                    },
                },
                yaxis: {
                    range: [-120, 20],
                    fixedrange: true,
                    title: {
                        text: 'Magnitude (dB)',
                        standoff: 0,
                    },
                },
                shapes: highlights,
                plot_bgcolor: '#cfd',
                paper_bgcolor: '#8fb',
            };
        }
        else if(graphType.current === 'spectrogram') {
            return {
                autosize: true,
                height: 320,
                title: {
                    text: 'Signal',
                    // automargin: false,
                },
                scene: {
                    xaxis: {
                        // autorange: true,
                        range: [0, recLen],
                        fixedrange: true,       // No zoom
                        title: {
                            text: 'Time (s)',
                            standoff: 0,
                        },
                        backgroundcolor: '#cdf',
                        showbackground: true,
                        visible: false,
                    },
                    yaxis: {
                        range: [400, 20000],
                        fixedrange: true,       // No zoom
                        title: {
                            text: 'Frequency (Hz)',
                            standoff: 0,
                        },
                        backgroundcolor: '#cdf',
                        showbackground: true,
                        visible: false,
                    },
                    zaxis: {
                        nticks: 7,
                        range: [-80, 0],
                        fixedrange: true,
                        autorange: false,       // No zoom
                        title: {
                            text: 'Magnitude (dB)',
                            standoff: 0,
                        },
                        backgroundcolor: '#cdf',
                        showbackground: true,
                        visible: false,
                    },
                    aspectmode: 'manual',
                    aspectratio: {
                        x: 2,
                        y: 1,
                        z: 1,
                    },
                    // bgcolor: '#cdf',
                    camera: {
                        eye: {
                            x: 0,
                            y: 0,
                            z: 2,
                        },
                        up: {
                            x: 0,
                            y: 1,
                            z: 0,
                        },
                        // projection: {
                        //     type: 'orthographic',
                        // },
                    }
                },
                margin: {
                    // autoexpand: false,
                    t: 40,
                    b: 10,
                    l: 10,
                    r: 10,
                },
                showlegend: false,
                paper_bgcolor: '#8bf',
            };
        }
        else
        {
            return {
                autosize: true,
                height: 320,
                title: {
                    text: 'Signal',
                    // automargin: false,
                },
                margin: {
                    // autoexpand: false,
                    t: 40,
                    b: 40,
                    l: 60,
                    r: 20,
                },
                showlegend: false,
                xaxis: {
                    // autorange: true,
                    range: [0, recLen],
                    fixedrange: true,
                    title: {
                        text: 'Time (s)',
                        standoff: 0,
                    },
                },
                yaxis: {
                    range: [-100, 0],
                    fixedrange: true,
                    title: {
                        text: 'Magnitude (dB)',
                        standoff: 0,
                    },
                },
                plot_bgcolor: '#fcd',
                paper_bgcolor: '#f8b',
            };
        }
    }

    const initializePlot = (figure: Figure) => {
        console.log('initializePlot');
        figure.data=[
            {
                x: getFrequencies(20.0, 1200, true),
                y: getZeros(1200),
                type: 'scatter',
                mode: 'lines',
                marker: {color: 'blue'},
            },
            // {type: 'bar', x: [1, 2, 3], y: [2, 6, 3]},
        ];
        figure.layout=getLayout();
        setPData(figure.data);
        setPLayout(figure.layout);
        if(figure.frames) {
            setPFrames(figure.frames);
        }
        const config = {
            responsive: true,
            editable: false,
            autosizable: true,
            scrollZoom: false,
            showAxisDragHandles: false,
            showAxisRangeEntryBoxes: false,
            showLink: false,
            displayModeBar: false,
            displaylogo: false,
        }
        setPConfig(config);
    }

    const updatePlot = (figure: Figure) => {
        // setPData(figure.data);
        // setPLayout(figure.layout);
        // if(figure.frames)
        // {
        //     setPFrames(figure.frames);
        // }
    }

    const plotError = (err: Error) => {
        console.log('plotError: ' + err);
    }

    const plotRedrawn = () =>
    {
        console.log('plotRedrawn');
        return (
            <div></div>
        );
    }

    const updateSignalPlot = (xdata: Float32Array, ydata: Float32Array, zdata: number[][] | null, rev: number) => {
        if(zdata)
        {
            const data: Plotly.Data[]= [
                {
                    x: xdata,
                    y: ydata,
                    z: zdata,
                    type: 'surface',
                    showlegend: false,
                    showscale: false,
                },
                // {type: 'bar', x: [1, 2, 3], y: [2, 6, 3]},
            ];
            setPData(data);
        }
        else
        {
            const data: Plotly.Data[]= [
                {
                    x: xdata,
                    y: ydata,
                    type: 'scatter',
                    mode: 'lines',
                    marker: {color: 'blue'},
                },
                // {type: 'bar', x: [1, 2, 3], y: [2, 6, 3]},
            ];
            setPData(data);
        }

        const layout = getLayout();
        layout.datarevision = rev; // Ensure a redraw with an updated datarevision
        setPLayout(layout);
    }

    const onAudioInput = ( evt: any ) => {
        // 'evt.data' is an integer array containing raw audio data
        //
        console.log(evt);
        // console.log( "Audio data received: " + evt.data.length + " samples" );
        // console.log( "Audio data min: " + Math.min(evt.data) + ", max: " + Math.max(evt.data) );

        // ... do something with the evt.data array ...
    }

    const onAudioInputError = ( error: any ) => {
        alert( "onAudioInputError event received: " + JSON.stringify(error) );
    };

    const getPlatformInfo = () => {
        const platforms = getPlatforms();
        // console.log('getPlatformInfo');
        // console.log(platforms);

        return (
            <div></div>
        );
    }


    const getTestButton = () => {
        if(isIOSWeb) {
            return (
                <IonButtons slot="end">
                    <IonButton fill={testing ? "outline" : "solid"}
                               onTouchStart={() => toggleMicrophone()}>{testing ? "Stop" : "Start"}</IonButton>
                </IonButtons>
            );
        }
        else if(testing)
        {
            return (
                <IonButtons slot="end">
                    <IonButton className='test-button' fill="outline" color="secondary"
                               onClick={() => {stopMicrophone()}}>
                        Stop
                    </IonButton>
                </IonButtons>
            );
        }
        else
        {
            return (
                <IonButtons slot="end">
                    <IonButton className='test-button' fill="solid" color="secondary"
                               onClick={() => {getMicrophone()}}>
                        Test
                    </IonButton>
                </IonButtons>
            );
        }
    }

    const getShareButton = () => {
        return (
            <IonButtons slot="end">
                <IonButton className='test-button' fill="outline" color="secondary"
                           onClick={() => {handleShareImage()}}>
                    Share
                </IonButton>
            </IonButtons>
        );
    }

    const handleDownloadImage = async () => {
        const element = document.getElementById('to-share');
        if(element) {
            const canvas = await html2canvas(element);

            const data = canvas.toDataURL('image/png');
            const link = document.createElement('a');

            if (typeof link.download === 'string') {
                link.href = data;
                link.download = 'image.png';

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                window.open(data);
            }
        }
    };

    const handleShareImage = async () => {
        const element = document.getElementById('to-share');
        if(element) {
            const canvas = await html2canvas(element);
            canvas.toBlob(async (blob) => {
                // Even if you want to share just one file you need to
                // send them as an array of files.
                if(blob) {
                    const files = [new File([blob], 'image.png', {type: blob.type})]
                    const shareData = {
                        text: 'SignalScope',
                        title: 'SignalScope',
                        files,
                    }
                    if (navigator.canShare(shareData)) {
                        try
                        {
                            await navigator.share(shareData)
                        }
                        catch (err)
                        {
                            if(err instanceof Error) {
                                if (err.name !== 'AbortError') {
                                    console.error(err.name, err.message)
                                }
                            }
                        }
                    } else {
                        console.warn('Sharing not supported', shareData)
                    }
                }
            });
        }
    }

    return (
        // eslint-disable-next-line react/jsx-no-undef
        <Fragment>
            {/*{getPlatformInfo()}*/}
            {/*<MultiPlot freqs={pFreqs.current} times={pTimes.current} levels={pLevel.current.slice(0, pTimeIdx.current+1)} wave={pWave.current} mag={pMag.current} magz={pMagz.current}></MultiPlot>*/}
            <div className="responsePlot" id='to-share'>
                <Plot
                    style={{width: '100%'}}
                    data={pData}
                    layout={pLayout}
                    revision={pRev.current}
                    frames={pFrames}
                    config={pConfig}
                    onInitialized={(figure) => initializePlot(figure)}
                    onUpdate={(figure) => updatePlot(figure)}
                    // onError={(err) => plotError(err)}
                    // debug={true}
                    // onRedraw={() => plotRedrawn()}
                />
            </div>
            <IonFooter>
                <IonToolbar>
                    <IonSegment value={graphType.current} onIonChange={(ev) => {graphTypeAction(ev);}}>
                        <IonSegmentButton value="levelhistory">
                            <IonLabel>Level History</IonLabel>
                        </IonSegmentButton>
                        <IonSegmentButton value="spectrum">
                            <IonLabel>Spectrum</IonLabel>
                        </IonSegmentButton>
                        <IonSegmentButton value="spectrogram">
                            <IonLabel>Spectrogram</IonLabel>
                        </IonSegmentButton>
                    </IonSegment>
                    {getTestButton()}
                </IonToolbar>
                <IonToolbar>
                    {getShareButton()}
                </IonToolbar>
            </IonFooter>
        </Fragment>
    );
}

export default AudioHandler;
